import React, { useEffect } from "react";
import { Button, Input } from "@postcheesy/ui-lib/atoms";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@postcheesy/ui-lib/molecules";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown, Heart, HeartCrack, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@postcheesy/core-utils";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import getCheeses, { CheeseItem } from "../../api/getCheeses";

export const apiUrl = import.meta.env.VITE_API_URL;
export const TableProduct = () => {

  const [cheeses, setCheeses] = React.useState<CheeseItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const getCheeseHistory = async () => {
      setIsLoading(true);
      const fetchedCheeses = await getCheeses();
      if (fetchedCheeses) {
        setCheeses(fetchedCheeses);
      }
      setIsLoading(false);
    };

    getCheeseHistory();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Skeleton className="w-16 h-16 rounded-full" />
      </div>
    );
  }

  return (
    <>
      <div className={cn("flex flex-col space-y-1.5")}>
        <h3
          className={cn("text-4xl font-semibold leading-none tracking-tight text-foreground serif")}
        >
          Mes Fromages
        </h3>
        <p className={cn("text-sm text-foreground")}>
          Suivez votre historique et découvrez vos fromages
        </p>
      </div>
      <DataTableDemo data={cheeses} />
    </>
  );
};
interface InputFilterProps {
  setColumnFilters: React.Dispatch<React.SetStateAction<{ id: string; value: unknown }[]>>;
}
export const InputFilter: React.FC<InputFilterProps> = ({ setColumnFilters }) => {
  const [search, setSearch] = React.useState("");
  const [timer, setTimer] = React.useState<number | null>(null);

  useEffect(() => {
    if (timer) {
      clearTimeout(timer);
    }
    const newTimer = window.setTimeout(() => {
      setColumnFilters((prev) => [...prev.filter(f => f.id !== 'fromageNom'), { id: 'fromageNom', value: search }]);
    }, 500);
    setTimer(newTimer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [search]);

  return (
    <Input
      placeholder="Recherche..."
      type="search"
      value={search ?? ""}
      onChange={(event) => setSearch(event.target.value)}
      className="max-w-sm "

    />
  );
};
const getSortingIcon = (isSorted: "asc" | "desc" | false) => {
  if (isSorted === "asc") {
    return <ArrowUp className="ml-2 h-4 w-4" />;
  }
  if (isSorted === "desc") {
    return <ArrowDown className="ml-2 h-4 w-4" />;
  }
  return <ArrowUpDown className="ml-2 h-4 w-4" />;
};

export const columns: ColumnDef<CheeseItem>[] = [
  {
    accessorKey: "Image",
    header: ({ column }) => {
      return (
        <Button
          className="hidden"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        />
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">
        <img
          alt="Product image"
          className="aspect-square h-12 w-12 min-w-12 rounded-md object-cover lg:h-16 lg:w-16 lg:min-w-16"
          src={`${apiUrl}/fromage/${row.original.id}/image?x=100&y=100`}
          onError={(e) => { e.currentTarget.src = `${apiUrl}/fromage/1/image?x=100&y=100` }}
          loading="lazy"
        />
      </div>
    ),
  },
  {
    accessorKey: "favorite",
    header: "Favorite",
    cell: ({ row }) => (row.original.favorite ? "Yes" : "No"),
  },
  {
    accessorKey: "disliked",
    header: "Disliked",
    cell: ({ row }) => (row.original.disliked ? "Yes" : "No"),
  },
  {
    accessorKey: "favoris",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Favoris
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => {
      const like = row.getValue("favorite");
      const dislike = row.getValue("disliked");
      const sentiment = like ? <Heart size={35} color="#870037" /> : dislike ? <HeartCrack size={35} color="#870037" /> : "";
      return <div className="px-4 capitalize flex items-center justify-center md:justify-start">{sentiment}</div>;
    },
    sortingFn: (rowA, rowB) => {
      const aLike = rowA.original.favorite ? 1 : rowA.original.disliked ? -1 : 0;
      const bLike = rowB.original.favorite ? 1 : rowB.original.disliked ? -1 : 0;
      return bLike - aLike; ///// Sorting by favorite > neutral > disliked
    }
  },
  {
    accessorKey: "fromageNom",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nom
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("fromageNom")}</div>
    ),
    sortingFn: (a, b) => {
      return (a.getValue("fromageNom") as string).localeCompare(
        b.getValue("fromageNom") as string,
        'fr',
        { sensitivity: 'base' }
      );// sort with french in mind
    }
  },
  {
    accessorKey: "dateDernierAchat",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Date
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => {
      const product = row.original;
      return (
        <div className="px-4 capitalize">
          {format(new Date(product?.dateDernierAchat ?? ""), "yyyy/MM/dd")}
          <br />
        </div>
      );
    },
  },
  {
    accessorKey: "paysDescription",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Origine
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("paysDescription")}</div>
    ),
  },

  {
    accessorKey: "laitDescription",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Lait
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("laitDescription")}</div>
    ),
  },
  {
    accessorKey: "traitementDescription",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Traitement
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">
        {row.getValue("traitementDescription")}
      </div>
    ),
  },

  {
    accessorKey: "regionDescription",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Region
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("regionDescription")}</div>
    ),
  },
  {
    accessorKey: "quantiteAnnee",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quantitée par année
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("quantiteAnnee")}</div>
    ),
  },
  {
    accessorKey: "nombreAchatAnnee",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nombre achat année
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("nombreAchatAnnee")}</div>
    ),
  },
  {
    accessorKey: "nombreAchat",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Nombre d'achat
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("nombreAchat")}</div>
    ),
  },
  {
    accessorKey: "quantiteTotale",
    header: ({ column }) => {
      const isSorted = column.getIsSorted();
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Quantité Totale
          {getSortingIcon(isSorted)}
        </Button>
      );
    },
    cell: ({ row }) => (
      <div className="px-4 capitalize">{row.getValue("quantiteTotale")}</div>
    ),
  },
];

export function DataTableDemo({ data }: { data: CheeseItem[] }) {
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: "dateDernierAchat",
      desc: true,
    }
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      fromageNom: true,
      fromageDescription: false,
      laitDescription: false,
      familleDescription: false,
      traitementDescription: false,
      paysDescription: true,
      regionDescription: false,
      quantiteTotale: false,
      quantiteAnnee: false,
      nombreAchat: false,
      nombreAchatAnnee: false,
      dateDernierAchat: true,
      favoris: true,
      favorite: false,
      disliked: false,
    });
  const [rowSelection, setRowSelection] = React.useState({});
  const navigate = useNavigate();

  const columnNames: Record<string, string> = {
    fromageNom: "Nom",
    laitDescription: "Lait",
    familleDescription: "Famille",
    traitementDescription: "Traitement",
    paysDescription: "Pays",
    regionDescription: "Région",
    quantiteTotale: "Quantité Totale",
    quantiteAnnee: "Quantité Annuelle",
    nombreAchat: "Nombre d'Achats",
    nombreAchatAnnee: "Achats Année",
    dateDernierAchat: "Dernier Achat",
  };

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full overflow-hidden">
      <div className="flex items-center py-4">
        <InputFilter setColumnFilters={setColumnFilters} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Information <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter(
                (column) =>
                  column.getCanHide() &&
                  !["favorite", "disliked"].includes(column.id)
              )
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize hover:bg-primary-foreground"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {columnNames[column.id] || column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border border-muted-foreground bg-primary-foreground">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() =>
                    navigate(`/product/${row.original.id}`)
                  }
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Aucun résultat
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">

        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}